import userTypes from './userTypes';

/* StorefrontUserDTO */

/**
 * A storefront user.
 *
 * @property {number} id - (Read only) The user's id.
 * @property {string} firstName - (Read only) The user's first name.
 * @property {string} lastName - (Read only) The user's last name.
 * @property {string} name - (Read only) The user's full name.
 * @property {string} email - (Read only) The user's email address.
 * @property {string} phone - (Read only) The user's phone number.
 * @property {string} username - (Read only) The user's username. Keep in mind that admin user's usernames are from Collaterate, and not the storefront itself.
 * @property {string} company - (Read only) The user's company.
 * @property {boolean} isSelf - (Read only) Is this user the same as the currently logged in user?
 * @property {boolean} isStorefrontUser - (Read only) Is this user a storefront user?
 * @property {boolean} isAdminUser - (Read only) Is this user an admin user?
 * @property {?Collaterate.User~loyalty} loyalty - (Read only) The user's loyalty data. Null if not applicable.
 * @property {string[]} paymentTypeOptions - (Read only) The payment types this user is permitted to check out with, e.g. ["CREDIT_CARD", "TERMS"]. Empty unless the user was fetched with {@link Collaterate.User.getUser Collaterate.User.getUser}.
 * @property {boolean} requiresDivision - (Read only) Whether this user must order on behalf of a division.
 * @property {boolean} canOrderForAllDivisions - (Read only) Whether this user may order for any division on the site.
 * @property {boolean} canOrderForAssignedDivisions - (Read only) Whether this user may order only for the divisions they are assigned to.
 */

class User {
    constructor (data) {
        this.id = data.id;
        this.firstName = data.firstName;
        this.lastName = data.lastName;
        this.name = data.name;
        this.email = data.email;
        this.phone = data.phone;
        this.username = data.username;
        this.company = data.company || '';
        this.isSelf = data.self;
        this.isStorefrontUser = data.type === userTypes.STOREFRONT;
        this.isAdminUser = data.type === userTypes.ADMIN;
        this.loyalty = data.loyalty
            ? Object.freeze({
                name: data.loyalty.tierName,
                color: data.loyalty.tierColor,
                availablePoints: data.loyalty.availablePoints,
                pendingPoints: data.loyalty.pendingPoints,
                lastUpdated: Object.freeze(new Date(data.loyalty.lastUpdated)),
            })
            : null;

        // Checkout related. Only the full StorefrontUserDTO carries these, so they are defaulted rather
        // than left undefined for the lighter user shapes returned by login and registration.
        this.paymentTypeOptions = Object.freeze(data.paymentTypeOptions ? [...data.paymentTypeOptions] : []);
        this.requiresDivision = Boolean(data.requireDivision);
        this.canOrderForAllDivisions = Boolean(data.orderForAllDivisions);
        this.canOrderForAssignedDivisions = Boolean(data.orderForAssignedDivisions);

        Object.freeze(this);
    }
}

export default User;

/**
 * Ojevt containing information about a user's loyalty tier
 * @typedef {Object} Collaterate.User~loyalty
 * @property {string} tierId - The user's tier id.
 * @property {string} tierName - The user's tier name.
 * @property {string} tierColor - The user's tier color in hexadecimal format.
 * @property {number} availablePoints - The user's available points.
 * @property {number} pendingPoints - The user's pending points.
 * @property {Date} lastUpdated - The user's last updated date as a Date object.
 */
